import React from 'react';
import { observer } from 'mobx-react';
import { computed, observable } from 'mobx';
import ErrorBoundary from 'components/hoc/error_boundary.js';
import { AppUI, PrivacySettingsState, PrivacySettingsUI } from '@seedlang/state';
import styled from '@emotion/styled';
import { flexCenter } from '@seedlang/style_mixins';

const VideoWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  margin: 0 auto;
  
  &, iframe {
    border-radius: 10px;
    overflow: hidden;
  }
`;

const FloatingVideo = styled.div`
  position: ${props => props.isFloating ? 'fixed' : 'relative'};
  bottom: ${props => props.isFloating ? '20px' : 'auto'};
  left: ${props => props.isFloating ? '20px' : 'auto'};
  z-index: ${props => props.isFloating ? '1000' : 'auto'};
  max-width: ${props => props.isFloating ? '540px' : ''};
  max-height: ${props => props.isFloating ? '300px' : ''};
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  transition-property: position, bottom, left, z-index, box-shadow, border-radius;
  box-shadow: ${props => props.isFloating ? '0 4px 12px rgba(0, 0, 0, 0.15)' : 'none'};
  will-change: transform, opacity;
  transform: ${props => props.isFloating ? 'scale(0.65)' : 'none'};
  transform-origin: bottom left;
  backface-visibility: hidden;
  width: 100%;
  height: 100%;
`;

const PrivacyNotice = styled.div`
  width: 100%;
  height: fit-content;
  padding: 1em;
  text-align: center;
  font-size: 14px;
  line-height: 1.5;
  text-wrap: balance;
  display: grid;
  place-content: center;
  position: relative;
  .background {
    position: absolute;
    inset: 0;
    z-index: 1;
    background-color: #DDD;
    ${({ placeholderImageUrl }) => placeholderImageUrl && `
      background-image: url('${placeholderImageUrl}');
      background-size: cover;
      filter: blur(5px);
    `}
  }
  .notice-text {
    z-index: 2;
    padding: 1em;
    background: rgba(255,255,255,0.9);
    border-radius: 0.5em;
    .title {
      font-size: 120%;
      font-weight: bold;
    }
  }
`;

const PrivacyButton = styled.div`
  display: inline-block;
  background: ${props => props.background};
  color: ${props => props.color};
  width: ${props => props.width};
  margin: ${props => props.margin};
  padding: 5px;
  font-weight: bold;
  cursor: pointer;
  border-radius: 10px;
`;

const CloseButton = styled.div`
  position: absolute;
  top: -10px;
  right: -10px;
  width: 40px;
  height: 40px;
  font-size: 24px;
  background: #000;
  border-radius: 50%;
  ${flexCenter()}
  cursor: pointer;
  color: #fff;
  z-index: 2;
`;

function getDismissedYoutubeVideoUrls() {
  const dismissedYoutubeVideoUrls = sessionStorage.getItem('dismissedYoutubeVideoUrls');
  if (!dismissedYoutubeVideoUrls) return [];
  return JSON.parse(dismissedYoutubeVideoUrls);
}

function addDismissedYoutubeVideoUrl(url) {
  const dismissedYoutubeVideoUrls = getDismissedYoutubeVideoUrls();
  sessionStorage.setItem(
    'dismissedYoutubeVideoUrls',
    JSON.stringify([...dismissedYoutubeVideoUrls, url]),
  );
}

@observer
class YoutubeVideo extends React.Component {
  @observable isOutOfView = false;
  @observable floatingDisabledByUser = false;

  constructor(props) {
    super(props);
    this.wrapperRef = React.createRef();
  }

  @computed get isFloating() {
    return !AppUI.layout.isMobile && this.isOutOfView && !this.floatingDisabledByUser && this.props.allowFloating;
  }

  componentDidMount() {
    const dismissedYoutubeVideoUrls = getDismissedYoutubeVideoUrls();
    if (dismissedYoutubeVideoUrls.includes(this.props.url)) {
      this.floatingDisabledByUser = true;
    }

    if (AppUI.layout.isMobile || !this.props.allowFloating || this.floatingDisabledByUser) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const newIsOutOfView = entry.intersectionRatio < 0.4;
        if (newIsOutOfView !== this.isOutOfView) {
          this.isOutOfView = newIsOutOfView;
        }
      },
      {
        threshold: [0, 0.4, 0.6, 1],
        rootMargin: '-10% 0px',
      },
    );

    if (this.wrapperRef.current) {
      observer.observe(this.wrapperRef.current);
    }

    return () => observer.disconnect();
  }

  handleCloseFloating = () => {
    this.floatingDisabledByUser = true;
    addDismissedYoutubeVideoUrl(this.props.url);
  };

  render() {
    const videoContent = PrivacySettingsState.isYoutubeAllowed ? (
      <div className='youtube-video' style={{ height: '100%' }}>
        <iframe
          src={`${this.props.url}?rel=0&autoplay=${this.props.autoPlay ? 1 : 0}`}
          style={{ width: '100%', height: '100%' }}
          allowFullScreen
          frameBorder='0'
          title='Video'
          allow='autoplay'
        />
      </div>
    ) : (
      <PrivacyNotice
        className='youtube-video'
        placeholderImageUrl={this.props.placeholderImageUrl}
      >
        <div className='background'></div>
        <div className='notice-text'>
          <p className='title'>Opt in to watch YouTube videos</p>
          <p>To respect your privacy, we do not load YouTube videos automatically. YouTube may collect personal data and track your activity if videos are played directly from our site.</p>
          <p>If you would like to watch the video, please click the button below to opt-in and enable the YouTube content.</p>
          <PrivacyButton
            background='#0584a1'
            color='#FFF'
            onClick={PrivacySettingsUI.showModal}
            width={AppUI.layout.isMobile ? '100%' : '200px'}
            margin={AppUI.layout.isMobile ? '5px 0 0 0' : '0 0 0 5px'}
          >
            Edit Privacy Preferences
          </PrivacyButton>
        </div>
      </PrivacyNotice>
    );

    return (
      <VideoWrapper ref={this.wrapperRef}>
        <FloatingVideo isFloating={this.isFloating}>
          {videoContent}
          {this.isFloating && (
            <CloseButton onClick={this.handleCloseFloating}>
              <i className='fa fa-times'></i>
            </CloseButton>
          )}
        </FloatingVideo>
      </VideoWrapper>
    );
  }
}

export default ErrorBoundary(YoutubeVideo);
